import {
  calculateWorkOrderCosts,
  type SnapOperationCostParams,
  type SnapWorkOrderCostResults,
} from '@calculations/cost';
import {
  QtyPerType,
  type RunRateType,
  runRateTypeSchema,
  type ScrapYieldType,
} from '@snap-types/models/enums';
import { Decimal, ZERO } from '@snap-types/types/decimal';

export type ConfiguratorCostsOpInput = {
  resourceId: string;
  sequenceNum: number;
  run: number;
  runType: RunRateType;
  burdenPerOperation: number;
  burdenPerHrRun: number;
  burdenPerUnitRun: number;
  burdenPerHrSetup: number;
  runCostPerHr: number;
  runCostPerUnit: number;
  setupCostPerHr: number;
  setupHrs: number;
  serviceChargePerUnit: number;
  serviceBaseCharge: number;
  serviceMinCharge: number;
  serviceId: string;
  scrapYieldType: ScrapYieldType;
  scrapYieldPercent: number;
  fixedScrap: number;
};

export type ConfiguratorCostsInput = ConfiguratorCostsOpInput[];

export type ConfiguratorOperationCosts = SnapOperationCostParams[];

export type ConfiguratorCostsOutput = SnapWorkOrderCostResults;

export function configuratorOpToSnap(
  quantity: number,
  op: ConfiguratorCostsOpInput,
  matUnitCost?: number,
  matQtyPerPiece?: number,
  washerMatUnitCost?: number | null,
): SnapOperationCostParams {
  const runRateType = runRateTypeSchema.parse(op.runType);
  let pieceIdx = 0;

  /**
   * Create dummy requirements here to calculate material cost.
   * The only relevant fields for us here are unitsPerPiece, matUnitCost, washerQtyPer and washerMatUnitCost for now
   */
  const requirements: SnapOperationCostParams['snapRequirements'] = [];

  if (matUnitCost && quantity && matQtyPerPiece) {
    requirements.push({
      unitMaterialCost: new Decimal(matUnitCost),
      unitLaborCost: ZERO,
      unitBurdenCost: ZERO,
      unitServiceCost: ZERO,
      qtyPer: new Decimal(matQtyPerPiece),
      qtyPerType: QtyPerType.START,
      fixedQty: ZERO,
      fixedCost: ZERO,
      scrapPercent: ZERO,
      pieceNo: ++pieceIdx * 10,
    });
  }

  if (quantity && washerMatUnitCost) {
    requirements.push({
      unitMaterialCost: new Decimal(washerMatUnitCost),
      unitLaborCost: ZERO,
      unitBurdenCost: ZERO,
      unitServiceCost: ZERO,
      qtyPer: new Decimal('1'),
      qtyPerType: QtyPerType.START,
      fixedQty: ZERO,
      fixedCost: ZERO,
      scrapPercent: ZERO,
      pieceNo: ++pieceIdx * 10,
    });
  }

  return {
    resourceId: op.resourceId,
    run: new Decimal(op.run),
    runCostPerHr: new Decimal(op.runCostPerHr),
    runCostPerUnit: new Decimal(op.runCostPerUnit),
    runType: runRateType,
    sequenceNo: op.sequenceNum,
    snapRequirements: requirements,
    burPerHrRun: new Decimal(op.burdenPerHrRun),
    burPerHrSetup: new Decimal(op.burdenPerHrSetup),
    // @TODO: NT - Where should this come from?
    // It looks like this isn't used for PCS_PER_HR calculations
    loadSizeQty: new Decimal(1),
    scrapYieldType: op.scrapYieldType,
    scrapYieldPct: new Decimal(op.scrapYieldPercent),
    fixedScrapUnits: new Decimal(op.fixedScrap),
    serviceBaseCharge: new Decimal(op.serviceBaseCharge),
    serviceMinCharge: new Decimal(op.serviceMinCharge),
    serviceChargePerUnit: new Decimal(op.serviceChargePerUnit),
    serviceId: op.serviceId,
    setupHrs: new Decimal(op.setupHrs),
    setupCostPerHr: new Decimal(op.setupCostPerHr),
  };
}

export function calculateConfiguratorCosts(
  quantity: number,
  ops: ConfiguratorCostsInput,
  matUnitCost: number,
  matQtyPer: number,
  washerMatUnitCost: number | null,
): {
  snapOperations: SnapOperationCostParams[];
  workOrderCosts: SnapWorkOrderCostResults;
} {
  const snapOperations = ops.map((op) =>
    // only calculate requirements for op 10 because we only have one requirement material field right now
    // TODO: allow additional requirements for each op
    {
      if (op.sequenceNum === 10) {
        return configuratorOpToSnap(
          quantity,
          op,
          matUnitCost,
          matQtyPer,
          washerMatUnitCost,
        );
      }

      return configuratorOpToSnap(quantity, op);
    },
  );

  const workOrderCosts = calculateWorkOrderCosts(
    {
      quantity: new Decimal(quantity),
      snapOperations,
    },
    {
      // TODO(cwmanning): can this be dynamic?
      // this CalculationConfig was copied from the ADF site config in @api/config
      decimalPlaces: 4,
    },
  );

  return {
    snapOperations,
    workOrderCosts,
  };
}
